import { useState } from "react";
import { domainTags, hotRank, categories, feedItems, type FeedItem } from "@/mocks/portalData";

const catColor: Record<string, string> = {
  ent: "bg-primary-100 text-primary-700",
  celeb: "bg-accent-100 text-accent-800",
  brand: "bg-secondary-100 text-secondary-800",
  tech: "bg-background-200 text-foreground-700",
  sports: "bg-primary-50 text-primary-700",
  life: "bg-accent-50 text-accent-700",
};

export default function FeedSection() {
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");
  const [visible, setVisible] = useState(6);

  const list = feedItems.filter((it) => {
    const matchTab = tab === "all" || it.category === tab;
    const q = query.trim();
    const matchQuery =
      !q ||
      it.title.includes(q) ||
      it.excerpt.includes(q) ||
      it.source.includes(q) ||
      it.sourceHandle.includes(q) ||
      it.categoryTag.includes(q);
    return matchTab && matchQuery;
  });

  return (
    <section id="feed" className="bg-background-100/60 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="mb-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-primary-600">实时更新 · FRESH FEEDS</p>
            <h2 className="mt-1 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
              最新爆料流
            </h2>
          </div>
          <span className="hidden whitespace-nowrap rounded-full bg-accent-500/10 px-3 py-1.5 text-xs font-medium text-accent-800 sm:inline-flex">
            <i className="ri-eye-line mr-1 text-sm"></i>
            {list.length} 条线索
          </span>
        </div>

        <div className="mb-6 flex flex-col gap-3 border-b border-background-200 pb-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-1 rounded-full bg-background-50 p-1">
            {categories.map((c) => (
              <button
                key={c.key}
                onClick={() => {
                  setTab(c.key);
                  setVisible(6);
                }}
                className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors md:px-4 ${
                  tab === c.key
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:bg-background-200/60"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 rounded-full border border-background-300/60 bg-background-50 px-3.5 py-2 md:w-72">
            <i className="ri-search-line text-lg text-foreground-500"></i>
            <input
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setVisible(6);
              }}
              placeholder="筛选当前列表话题"
              className="w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
            />
            {query && (
              <button onClick={() => setQuery("")} aria-label="清除搜索" className="text-foreground-400 hover:text-foreground-700">
                <i className="ri-close-circle-fill text-lg"></i>
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {list.slice(0, visible).map((item: FeedItem) => (
            <FeedCard key={item.id} item={item} />
          ))}
        </div>

        {list.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-2xl bg-background-50 py-16 text-center">
            <i className="ri-inbox-line text-4xl text-foreground-300"></i>
            <p className="mt-3 text-sm text-foreground-500">该分类下暂无匹配的爆料内容，换个关键词试试吧。</p>
          </div>
        )}

        {list.length > visible && (
          <div className="mt-8 flex justify-center">
            <button
              onClick={() => setVisible((v) => v + 6)}
              className="flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300/70 bg-background-50 px-6 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <i className="ri-loader-4-line text-lg"></i>
              加载更多
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

function FeedCard({ item }: { item: FeedItem }) {
  return (
    <article className="group flex flex-col rounded-xl border border-background-200/70 bg-background-50 p-5 transition-shadow hover:shadow-[0_10px_30px_-20px_oklch(var(--foreground-950)/0.45)]">
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${catColor[item.category]}`}>
          {item.trending && <i className="ri-fire-fill text-xs"></i>}
          {item.categoryTag}
        </span>
        {item.trending && (
          <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-500/10 px-2 py-0.5 text-xs font-semibold text-primary-600">
            <i className="ri-hotspot-line text-xs"></i>
            {item.heat} 热度
          </span>
        )}
      </div>

      <h3 className="font-heading text-base font-bold leading-snug text-foreground-950 group-hover:text-primary-700 md:text-lg">
        {item.title}
      </h3>
      <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-foreground-600">{item.excerpt}</p>

      <div className="mt-4 flex items-center justify-between border-t border-background-200/70 pt-3">
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
            <i className="ri-user-star-line text-sm"></i>
          </span>
          <div className="min-w-0">
            <p className="truncate text-xs font-semibold text-foreground-800">{item.source}</p>
            <p className="truncate text-[11px] text-foreground-400">{item.sourceHandle}</p>
          </div>
        </div>
        <span className="flex shrink-0 items-center gap-1 whitespace-nowrap text-xs text-foreground-400">
          <i className="ri-time-line text-sm"></i>
          {item.time}
        </span>
      </div>
    </article>
  );
}